import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { addMinutes } from '@repo/date-time';
import { ParkTimeContractType } from '@repo/domain';
import {
  BusinessValidationCode,
  BusinessValidationError,
  useFetchReservations,
  useReservationActions,
} from '@repo/domain-service';
import { tr } from '@repo/locale';
import { TestId } from '@repo/test-id';

import { RESERVATION_FLOW_MODE_PARAM, ReservationFlowMode } from '@features/reservation/constants';

import { handleRepositoryError } from '@shared/utils/handleRepositoryError';
import { useSelectedParkingProperties } from '@shared/hooks/useSelectedParkingProperties';
import { useParkingSearchStore } from '@shared/stores/useParkingSearchStore';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

const RESERVATION_SCOPE = { withParking: true, withVehicle: true, withSpot: true } as const;

interface EnsurePendingReservationResult {
  ensuring: boolean;
  blocked: boolean;
  continuing: boolean;
  showExisting: () => void;
  continueAnyway: () => void;
}

/**
 * Resolves the reservation flow against the backend (the single source of
 * truth — the frontend keeps no reservation state of its own), branching on how
 * the flow was entered:
 */
export function useEnsurePendingReservation(): EnsurePendingReservationResult {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchReservations = useFetchReservations();
  const { createReservation, cancelReservation } = useReservationActions();
  const selectedParking = useParkingSearchStore((state) => state.selectedParking);
  const parkTimeContract = useParkingSearchStore((state) => state.parkTimeContract);
  const selectedPriceRuleId = useParkingSearchStore((state) => state.selectedPriceRuleId);
  const setSelectedPriceRuleId = useParkingSearchStore((state) => state.setSelectedPriceRuleId);
  const selectedProperties = useSelectedParkingProperties();
  const [ensuring, setEnsuring] = useState<boolean>(true);
  const [blocked, setBlocked] = useState<boolean>(false);
  const [continuing, setContinuing] = useState<boolean>(false);
  const startedRef = useRef<boolean>(false);
  const blockingReservationIdRef = useRef<string>('');
  const createFromSelectionRef = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const isContinueMode = searchParams.get(RESERVATION_FLOW_MODE_PARAM) === ReservationFlowMode.Continue;
    const testId = TestId.root('reservation.flow');

    const findPendingReservation = async (): Promise<string> => {
      const reservations = await fetchReservations({ only: 'pending' }, RESERVATION_SCOPE);
      return reservations[0]?.id ?? '';
    };

    const leaveToMap = () => navigate(ROUTE_PATHS[RouteName.MAP], { replace: true });

    const blockOnExisting = (reservationId: string) => {
      blockingReservationIdRef.current = reservationId;
      setBlocked(true);
    };

    const switchToContinueMode = () => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(RESERVATION_FLOW_MODE_PARAM, ReservationFlowMode.Continue);
      setSearchParams(nextParams, { replace: true });
    };

    const createFromSelection = async () => {
      if (!selectedParking.id) {
        leaveToMap();
        return;
      }

      const now = new Date();
      const futureStartAt =
        parkTimeContract.startAtTimestamp !== undefined && parkTimeContract.startAtTimestamp > now.getTime()
          ? new Date(parkTimeContract.startAtTimestamp)
          : undefined;

      const priceRuleId = selectedPriceRuleId;
      setSelectedPriceRuleId(undefined);

      try {
        if (priceRuleId !== undefined) {
          await createReservation({
            kind: 'package',
            parkingId: selectedParking.id,
            priceRuleId,
            spotId: selectedParking.spotId,
            startAt: futureStartAt,
          });
        } else {
          const startAt = futureStartAt ?? now;
          const endAt =
            parkTimeContract.type === ParkTimeContractType.FixedEnd
              ? new Date(parkTimeContract.endAtTimestamp)
              : addMinutes(startAt, parkTimeContract.duration);
          await createReservation({
            kind: 'standard',
            parkingId: selectedParking.id,
            startAt,
            endAt,
            spotId: selectedParking.spotId,
            properties: selectedProperties,
          });
        }
      } catch (error) {
        if (
          error instanceof BusinessValidationError &&
          error.code === BusinessValidationCode.reservationFailedDueToPendingReservationExists
        ) {
          blockOnExisting(await findPendingReservation());
          return;
        }

        handleRepositoryError(error, {
          fallback: tr('reservations:create_error.fallback'),
          testId,
          businessCodes: {
            reservationFailedDueToSpotTaken: tr('reservations:create_error.spot_taken'),
            reservationFailedDueToNoAvailableSpot: tr('reservations:create_error.no_available_spot'),
            reservationFailedDueToFutureReservationUnavailable: tr(
              'reservations:create_error.future_reservation_unavailable'
            ),
            reservationFailedDueToWaitingForPayment: tr('reservations:create_error.waiting_for_payment'),
            reservationFailedDueToPaymentFailed: tr('reservations:create_error.payment_failed'),
            reservationFailedDueToParkingNotFound: tr('reservations:create_error.parking_not_found'),
            reservationFailedDueToSpotNotFound: tr('reservations:create_error.spot_not_found'),
            reservationFailedDueToStepCompleted: tr('reservations:create_error.step_completed'),
            reservationFailedDueToReservationNotFound: tr('reservations:create_error.reservation_not_found'),
            reservationFailedDueToReservationOwnershipMismatch: tr(
              'reservations:create_error.reservation_ownership_mismatch'
            ),
            reservationFailedDueToReservationLockExpired: tr('reservations:create_error.reservation_lock_expired'),
            reservationFailedDueToReservationNotPending: tr('reservations:create_error.reservation_not_pending'),
            reservationFailedDueToReservationUserAlreadyAssigned: tr(
              'reservations:create_error.reservation_user_already_assigned'
            ),
            reservationFailedDueToVehicleNotFound: tr('reservations:create_error.vehicle_not_found'),
            reservationFailedDueToVehicleOwnershipMismatch: tr('reservations:create_error.vehicle_ownership_mismatch'),
            reservationFailedDueToBillingNotFound: tr('reservations:create_error.billing_not_found'),
            reservationFailedDueToBillingOwnershipMismatch: tr('reservations:create_error.billing_ownership_mismatch'),
            reservationFailedDueToRevolvingPeriodUnavailable: tr(
              'reservations:create_error.revolving_period_unavailable'
            ),
            reservationFailedDueToOther: tr('reservations:create_error.other'),
          },
        });
        useParkingSearchStore.getState().resetAfterReservation();
        leaveToMap();
        return;
      }

      if (await findPendingReservation()) {
        switchToContinueMode();
        setEnsuring(false);
      } else {
        leaveToMap();
      }
    };

    createFromSelectionRef.current = createFromSelection;

    const resolve = async () => {
      const pendingReservationId = await findPendingReservation();

      // CONTINUE: resume the existing reservation, or leave if there's none.
      if (isContinueMode) {
        if (pendingReservationId) {
          setEnsuring(false);
        } else {
          leaveToMap();
        }
        return;
      }

      // CREATE: one already exists, so the user has to resolve the conflict.
      if (pendingReservationId) {
        blockOnExisting(pendingReservationId);
        return;
      }

      await createFromSelection();
    };

    void resolve();
  }, [
    fetchReservations,
    createReservation,
    selectedPriceRuleId,
    setSelectedPriceRuleId,
    selectedParking,
    parkTimeContract,
    selectedProperties,
    navigate,
    searchParams,
    setSearchParams,
  ]);

  const showExisting = () => {
    navigate(ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS_PENDING], { replace: true });
  };

  const continueAnyway = async () => {
    if (continuing) return;
    setContinuing(true);

    try {
      await cancelReservation(blockingReservationIdRef.current);
    } catch (error) {
      handleRepositoryError(error, {
        fallback: tr('account:reservations.cancel_error'),
        testId: TestId.root('reservation.flow'),
      });
      setContinuing(false);
      return;
    }

    setBlocked(false);
    await createFromSelectionRef.current();
    setContinuing(false);
  };

  const handleContinueAnyway = () => {
    void continueAnyway();
  };

  return { ensuring, blocked, continuing, showExisting, continueAnyway: handleContinueAnyway };
}
